'use client';

import { useTranslations } from 'next-intl';
import { Input } from '@/components/ui/input/input';
import { type CurrencyOption } from '@impact/types';
import { useTenantSlug } from '@/hooks/use-tenant-slug';
import { getTenantConfig } from '@/tenants/config';

export interface AmountSelectorProps {
  currency: CurrencyOption;
  amounts: number[];
  selectedAmount: number | null;
  customAmount: string;
  onAmountSelect: (amount: number) => void;
  onCustomAmountChange: (value: string) => void;
  /** Visual theme override. Auto-resolves from tenant slug if omitted. */
  theme?: 'light' | 'dark';
}

export function AmountSelector({
  currency,
  amounts,
  selectedAmount,
  customAmount,
  onAmountSelect,
  onCustomAmountChange,
  theme,
}: AmountSelectorProps) {
  const tForm = useTranslations('donate.form');
  const tenantSlug = useTenantSlug();
  const resolvedTheme = theme ?? getTenantConfig(tenantSlug).donationTheme ?? 'dark';
  const isLight = resolvedTheme === 'light';

  // Light theme uses semantic `primary` tokens so each tenant's brand accent
  // colour flows through from the per-tenant CSS variables.
  const selectedClass = isLight
    ? 'bg-primary border-primary text-primary-foreground'
    : 'bg-dgr-gold border-dgr-gold text-stone-950';
  const unselectedClass = isLight
    ? 'bg-white border-primary/20 text-stone-900 hover:border-primary/60'
    : 'bg-stone-800 border-stone-700 text-white hover:border-stone-600';
  const prefixClass = isLight
    ? 'border-primary/30 bg-white text-stone-500'
    : 'border-stone-700 bg-stone-800 text-stone-400';
  const inputClass = isLight
    ? 'bg-white border-primary/30 text-stone-900 placeholder:text-stone-400 focus:ring-primary focus:border-primary'
    : 'bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 focus:ring-dgr-gold focus:border-dgr-gold';

  return (
    <>
      {/* Amount Selection Grid */}
      <div className="grid grid-cols-2 gap-2">
        {amounts.map((amount) => (
          <button
            key={amount}
            type="button"
            onClick={() => {
              onAmountSelect(amount);
              onCustomAmountChange('');
            }}
            className={`h-12 rounded-lg border text-sm font-semibold transition-all ${
              selectedAmount === amount && !customAmount ? selectedClass : unselectedClass
            }`}
          >
            {currency.symbol}
            {amount}
          </button>
        ))}
      </div>

      {/* Custom Amount with Currency Prefix Input Group */}
      <div className="flex">
        <span
          className={`inline-flex items-center px-3 rounded-l-lg border border-r-0 text-sm font-medium ${prefixClass}`}
        >
          {currency.symbol}
        </span>
        <Input
          type="number"
          placeholder={tForm('otherAmount')}
          value={customAmount}
          onChange={(e) => {
            onCustomAmountChange(e.target.value);
            onAmountSelect(0);
          }}
          className={`h-11 rounded-l-none ${inputClass}`}
        />
      </div>
    </>
  );
}
